import { useState, useEffect, useRef } from 'react';

const BINANCE_STREAMS = {
  'BTC/USD':  'btcusdt',
  'ETH/USD':  'ethusdt',
  'SOL/USD':  'solusdt',
  'BNB/USD':  'bnbusdt',
  'AAVE/USD': 'aaveusdt',
  'DOGE/USD': 'dogeusdt',
  'ALGO/USD': 'algousdt',
};

const useLivePrices = () => {
  const [prices, setPrices] = useState({
    'BTC/USD':  { price: 0, change: 0, loading: true },
    'ETH/USD':  { price: 0, change: 0, loading: true },
    'SOL/USD':  { price: 0, change: 0, loading: true },
    'BNB/USD':  { price: 0, change: 0, loading: true },
    'AAVE/USD': { price: 0, change: 0, loading: true },
    'DOGE/USD': { price: 0, change: 0, loading: true },
    'ALGO/USD': { price: 0, change: 0, loading: true },
  });

  const wsRef = useRef(null);

  useEffect(() => {
    const symbols = Object.values(BINANCE_STREAMS);
    const streamStr = symbols.map(s => `${s}@ticker`).join('/');
    const wsUrl = `wss://stream.binance.com:9443/stream?streams=${streamStr}`;

    const fetchRestFallback = async () => {
      try {
        const symbolsList = symbols.map(s => s.toUpperCase());
        const url = `https://api.binance.com/api/v3/ticker/24hr?symbols=${encodeURIComponent(JSON.stringify(symbolsList))}`;
        console.log('[LivePrices] Fetching from:', url);
        const res = await fetch(url);

        if (!res.ok) throw new Error(`API Error: ${res.status}`);
        const data = await res.json();
        console.log('[LivePrices] API Response:', data);

        if (!Array.isArray(data)) {
          console.error('[LivePrices] Invalid response, not an array:', typeof data, Object.keys(data || {}));
          return;
        }

        console.log('[LivePrices] Processing', data.length, 'tickers');
        data.forEach(ticker => {
          console.log('[LivePrices] Ticker:', ticker.symbol, 'Price:', ticker.lastPrice, 'Change:', ticker.priceChangePercent);
          const pair = Object.entries(BINANCE_STREAMS).find(([, v]) => v === ticker.symbol.toLowerCase())?.[0];
          if (pair) {
            console.log('[LivePrices] Mapped', ticker.symbol, 'to', pair);
            setPrices(prev => ({
              ...prev,
              [pair]: {
                price: parseFloat(ticker.lastPrice),
                change: parseFloat(ticker.priceChangePercent),
                loading: false
              },
            }));
          } else {
            console.warn('[LivePrices] No pair found for', ticker.symbol);
          }
        });
      } catch (err) { console.error('[LivePrices] REST API Error:', err.message, err); }
    };

    const connect = () => {
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        let wsConnected = false;

        ws.onopen = () => {
          wsConnected = true;
          console.log('[LivePrices] Binance WS connected');
        };

        ws.onmessage = (e) => {
          try {
            const msg = JSON.parse(e.data);
            const ticker = msg.data;
            if (!ticker) return;

            const symbol = ticker.s?.toLowerCase();
            const pair = Object.entries(BINANCE_STREAMS).find(([, v]) => v === symbol)?.[0];
            if (!pair) return;

            const price = parseFloat(ticker.c);
            const change = parseFloat(ticker.P);

            setPrices(prev => ({
              ...prev,
              [pair]: { price, change: parseFloat(change.toFixed(2)), loading: false },
            }));
          } catch {}
        };

        ws.onerror = (err) => {
          console.warn('[LivePrices] WS Error, using REST API fallback:', err);
          if (!wsConnected) fetchRestFallback();
        };

        ws.onclose = (e) => {
          console.warn('[LivePrices] WS closed, retrying...', e.code, e.reason);
          setTimeout(() => connect(), 5000);
        };
      } catch (err) {
        console.error('[LivePrices] WS Connection Error:', err);
        fetchRestFallback();
      }
    };

    connect();
    const restInterval = setInterval(fetchRestFallback, 5000); // Fallback every 5 seconds

    return () => {
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
      }
      clearInterval(restInterval);
    };
  }, []);

  return prices;
};

export default useLivePrices;